import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { DirectoryNodePermissions } from './types';

const CLIPBOARD_STORAGE_KEY = 'gd:directories:permissions-clipboard';

function isPermissionEntryList(value: unknown): boolean {
  return (
    Array.isArray(value) &&
    value.every(
      (entry) =>
        typeof entry === 'object' &&
        entry !== null &&
        typeof (entry as { id: unknown }).id === 'string' &&
        typeof (entry as { allowed: unknown }).allowed === 'boolean'
    )
  );
}

function isDirectoryNodePermissions(value: unknown): value is DirectoryNodePermissions {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.superadmin === 'boolean' &&
    isPermissionEntryList(candidate.positions) &&
    isPermissionEntryList(candidate.functionalActivities)
  );
}

function clonePermissions(permissions: DirectoryNodePermissions): DirectoryNodePermissions {
  return {
    superadmin: permissions.superadmin,
    positions: permissions.positions.map((entry) => ({ ...entry })),
    functionalActivities: permissions.functionalActivities.map((entry) => ({ ...entry })),
  };
}

function readStoredPermissions(): DirectoryNodePermissions | null {
  try {
    const raw = sessionStorage.getItem(CLIPBOARD_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    return isDirectoryNodePermissions(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeStoredPermissions(permissions: DirectoryNodePermissions | null) {
  try {
    if (!permissions) {
      sessionStorage.removeItem(CLIPBOARD_STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(CLIPBOARD_STORAGE_KEY, JSON.stringify(permissions));
  } catch {
    // A área de transferência é uma conveniência: sem sessionStorage ela vale só para a aba atual.
  }
}

/**
 * Área de transferência das permissões de diretório.
 *
 * Vive fora da tela para o que foi copiado sobreviver à navegação entre telas, e espelha em
 * `sessionStorage` para sobreviver também a um F5 — some quando a aba é fechada, que é o fim da
 * sessão do usuário.
 */
export const useDirectoryPermissionsClipboardStore = defineStore(
  'directory-permissions-clipboard',
  () => {
    const permissions = ref<DirectoryNodePermissions | null>(readStoredPermissions());

    const hasPermissions = computed(() => permissions.value !== null);

    function copy(next: DirectoryNodePermissions) {
      const stored = clonePermissions(next);
      permissions.value = stored;
      writeStoredPermissions(stored);
    }

    function clear() {
      permissions.value = null;
      writeStoredPermissions(null);
    }

    return {
      permissions,
      hasPermissions,
      copy,
      clear,
    };
  }
);
